import { createSignal, createEffect, onMount, onCleanup, Show, For, useContext } from "solid-js";
import JsSIP from "jssip";
import { getCurrentUserRaw, getUsersRaw, hasRole, UserContext } from "../auth.js";

JsSIP.debug.disable();
// JsSIP.debug.enable('JsSIP:*');

import { Box, Paper, IconButton, Typography, Button, TextField, List, ListItem, ListItemAvatar, Avatar, ListItemText, Fab } from "@suid/material";
import CallIcon from "@suid/icons-material/Call";
import MinimizeIcon from "@suid/icons-material/Minimize";
import CallEndIcon from "@suid/icons-material/CallEnd";
import VolumeUpIcon from "@suid/icons-material/VolumeUp";
import { getSipContacts } from "../sip.js";

export default function WebPhone() {
  const user = useContext(UserContext);
  const [isMinimized, setIsMinimized] = createSignal(true);
  const [status, setStatus] = createSignal("Disconnected");
  const [contacts, setContacts] = createSignal([]);
  const [dialNumber, setDialNumber] = createSignal("");

  const [callState, setCallState] = createSignal("idle");
  const [session, setSession] = createSignal(null);

  const [audioBlocked, setAudioBlocked] = createSignal(false);
  const [errorMessage, setErrorMessage] = createSignal("");

  const [streamReady, setStreamReady] = createSignal(false);
  const [wantsAudible, setWantsAudible] = createSignal(false);

  const [remoteUser, setRemoteUser] = createSignal(null);
  // Two dedicated elements: one for the ringtone, one for the remote call audio.
  // Keeping them separate removes the race where ringtone src/pause mutations
  // could interrupt an in-flight play() on the remote stream.
  let remoteAudioRef;
  let ringtoneRef;
  let ua;

  const attachStream = (stream) => {
    if (!remoteAudioRef) return;
    setStreamReady(false);
    remoteAudioRef.srcObject = stream;
    remoteAudioRef.muted = !wantsAudible();

    remoteAudioRef
      .play()
      .then(() => {
        setStreamReady(true);
        setAudioBlocked(false);
        if (wantsAudible()) {
          remoteAudioRef.muted = false;
        }
      })
      .catch((err) => {
        console.warn("Audio playback blocked:", err);
        setAudioBlocked(true);
      });
  };

  const retryPlayback = () => {
    if (!remoteAudioRef || !remoteAudioRef.srcObject) return;
    remoteAudioRef
      .play()
      .then(() => {
        setStreamReady(true);
        setAudioBlocked(false);
        if (wantsAudible()) remoteAudioRef.muted = false;
      })
      .catch((err) => {
        console.warn("Audio playback retry blocked:", err);
        setAudioBlocked(true);
      });
  };

  createEffect(() => {
    const ready = streamReady();
    const audible = wantsAudible();
    if (remoteAudioRef && ready && audible) {
      remoteAudioRef.muted = false;
    }
  });

  createEffect(() => {
    const state = callState();
    if (!ringtoneRef) return;

    if (state === "ringing") {
      ringtoneRef.src = "/ringtone.mp3";
      ringtoneRef.loop = true;
      ringtoneRef.muted = false;
      ringtoneRef.play().catch((err) => console.warn("Ringtone playback blocked:", err));
    } else {
      ringtoneRef.pause();
      ringtoneRef.currentTime = 0;
    }
  });

  const unlockAudio = () => {
    setWantsAudible(true);
    if (remoteAudioRef && remoteAudioRef.srcObject) {
      remoteAudioRef.muted = false;
      remoteAudioRef
        .play()
        .then(() => setAudioBlocked(false))
        .catch(() => setAudioBlocked(true));
    }
  };

  const wireRemoteAudio = (pc) => {
    let attached = false;
    const tryAttach = (stream) => {
      if (attached) return;
      attached = true;
      attachStream(stream);
    };

    pc.addEventListener("track", (trackEvent) => {
      const stream = trackEvent.streams[0] || new MediaStream([trackEvent.track]);
      tryAttach(stream);
    });

    const existingTracks = (pc.getReceivers?.() || [])
      .map((r) => r.track)
      .filter((t) => t && t.kind === "audio");
    if (existingTracks.length) {
      tryAttach(new MediaStream(existingTracks));
    }
  };

  const initializeUserAgent = async () => {
    try {
      const res = await fetch("/api/sip/authenticate?device=web");
      if (!res.ok) throw new Error(`SIP auth request failed (${res.status})`);
      const creds = await res.json();
      const sipHost = import.meta.env.DEV ? 'localhost' : 'sip.paxico.pk';

      const sipUrl = import.meta.env.DEV ? "ws://localhost:8089/ws" : "wss://sip.paxico.pk/signal"
      const socket = new JsSIP.WebSocketInterface(sipUrl);

      const configuration = {
        sockets: [socket],
        uri: `sip:${creds.username}@${sipHost}`,
        authorization_user: creds.username,
        password: creds.password,
        register: true,
      };

      ua = new JsSIP.UA(configuration);

      ua.on("connected", () => setStatus("Connected"));
      ua.on("disconnected", () => setStatus("Disconnected"));
      ua.on("registered", () => setStatus("Registered"));
      ua.on("unregistered", () => setStatus("Unregistered"));
      ua.on("registrationFailed", async (e) => {
        setStatus(`Reg Failed: ${e.cause}`);
        //fetch new creds
        const res = await fetch("/api/sip/authenticate?device=web");
        if (!res.ok) throw new Error(`SIP auth request failed (${res.status})`);
        const creds = await res.json();

        //update ua credentials
        ua.stop();
        ua.set('authorization_user', creds.username);
        ua.set('password', creds.password);

        ua.start();
      });


      ua.on("newRTCSession", (data) => {
        const activeSession = data.session;

        if (session() && session() !== activeSession) {
          activeSession.terminate();
          return;
        }

        setSession(activeSession);
        setCallState(activeSession.direction === "incoming" ? "ringing" : "calling");

        if (activeSession?.remote_identity?.uri?.user) {
          const remoteUserId = activeSession?.remote_identity?.uri?.user.split('_')[0];
          const r = contacts().find((c => c.id === remoteUserId));
          if (r) {
            setRemoteUser(r);
          }
        }

        const wireOnce = (pc) => {
          if (activeSession._audioWired) return;
          activeSession._audioWired = true;
          wireRemoteAudio(pc);
        };

        activeSession.on("accepted", () => {
          setCallState("in-call");
        });

        activeSession.on("progress", () => {
          if (activeSession.direction === "outgoing") {
            setCallState("ringing-outgoing");
          }
        });

        const resetAudioState = () => {
          if (remoteAudioRef) {
            remoteAudioRef.srcObject = null;
            remoteAudioRef.muted = true;
          }
          setStreamReady(false);
          setWantsAudible(false);
          setAudioBlocked(false);
        };

        activeSession.on("ended", () => {
          setCallState("idle");
          setSession(null);
          resetAudioState();
        });

        activeSession.on("failed", (e) => {
          setCallState("idle");
          setSession(null);
          resetAudioState();
          if (e?.cause) setErrorMessage(`Call failed: ${e.cause}`);
        });

        activeSession.on("peerconnection", (e) => {
          wireOnce(e.peerconnection);
        });

        if (activeSession.connection) {
          wireOnce(activeSession.connection);
        }
      });

      ua.start();
    } catch (err) {
      console.error("SIP Auth failed", err);
      setStatus("Auth Error");
      setErrorMessage("Could not connect to the phone system.");
    }
  }

  createEffect(async () => {
    if (!hasRole(user(), 'sip')) {
      console.log('skipping sip');
      return;
    }

    try {
      const sipUsers = await getSipContacts();
      setContacts(sipUsers);
    } catch (err) {
      console.error("Failed to load contacts", err);
      setErrorMessage("Failed to load contacts.");
    }

    initializeUserAgent();
  });

  createEffect(() => {
    if (callState() === "ringing") {
      setIsMinimized(false);
    }
  });

  createEffect(() => {
    if (!errorMessage()) return;
    const timer = setTimeout(() => setErrorMessage(""), 5000);
    onCleanup(() => clearTimeout(timer));
  });

  onCleanup(() => {
    const current = session();
    if (current) {
      try {
        current.terminate();
      } catch (_) {
        /* no-op */
      }
    }
    if (ua) {
      try {
        ua.stop();
      } catch (_) {
        /* no-op */
      }
    }
  });

  const sipRtcOptions = {
    mediaConstraints: { audio: true, video: false },
    pcConfig: { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] }
  };

  const makeCall = (target) => {
    unlockAudio();
    if (!ua || !target || callState() !== "idle") return;



    ua.call(`sip:${target}@localhost`, sipRtcOptions);
    setIsMinimized(false);
    setDialNumber("");
  };

  const endCall = () => {
    unlockAudio();
    const current = session();
    if (current) current.terminate();
  };

  const answerCall = () => {
    unlockAudio();
    const current = session();
    if (current) current.answer(sipRtcOptions);
  };

  return (
    <Show when={user() && hasRole(user(), 'sip')}>
      <audio ref={remoteAudioRef} autoplay muted playsinline style={{ display: "none" }} />
      <audio ref={ringtoneRef} playsinline style={{ display: "none" }} />

      <Box sx={{ position: "fixed", bottom: 20, right: 20, zIndex: 9999 }}>
        <Show
          when={!isMinimized()}
          fallback={
            <Fab color="primary" onClick={() => setIsMinimized(false)}>
              <CallIcon />
            </Fab>
          }
        >
          <Paper elevation={6} sx={{ width: 300, height: 500, display: "flex", flexDirection: "column", borderRadius: 5 }}>
            <Box sx={{ p: 1, bgcolor: "primary.main", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center", borderTopLeftRadius: 20, borderTopRightRadius: 20 }}>
              <Typography variant="caption" sx={{ mr: 2 }}>{callState()}</Typography>
              <Box>
                <IconButton size="small" color="inherit" onClick={() => setIsMinimized(true)}>
                  <MinimizeIcon />
                </IconButton>
              </Box>
            </Box>

            <Show when={audioBlocked() && callState() === "in-call"}>
              <Box
                sx={{ p: 1, bgcolor: "warning.light", display: "flex", alignItems: "center", justifyContent: "center", gap: 1, cursor: "pointer" }}
                onClick={retryPlayback}
              >
                <VolumeUpIcon fontSize="small" />
                <Typography variant="body2">Tap to enable audio</Typography>
              </Box>
            </Show>

            <Show when={callState() !== "idle"}>
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 2 }}>
                <Typography variant="h5">
                  {callState() === "ringing" && "Incoming Call..."}
                  {callState() === "calling" && "Calling..."}
                  {callState() === "ringing-outgoing" && "Ringing..."}
                  {callState() === "in-call" && "In Call"}
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>{remoteUser()?.name}</Typography>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <Show when={callState() === "ringing"}>
                    <Fab color="success" onClick={answerCall}>
                      <CallIcon />
                    </Fab>
                  </Show>
                  <Fab color="error" onClick={endCall}>
                    <CallEndIcon />
                  </Fab>
                </Box>
              </Box>
            </Show>

            <Show when={callState() === "idle"}>
              <Box sx={{ flex: 1, overflowY: "auto" }}>
                <List>
                  <For each={contacts()}>
                    {(user) => (
                      <ListItem button onClick={() => makeCall(user.id)}>
                        <ListItemAvatar>
                          <Show when={user.avatar} fallback={<Avatar alt={user.name}>{user.name.charAt(0)}</Avatar>}>
                            <img
                              src={user.avatar}
                              alt={user.name}
                              referrerpolicy="no-referrer"
                              style={{
                                width: "40px",
                                height: "40px",
                                "border-radius": "50%",
                                "object-fit": "cover"
                              }}
                            />
                          </Show>
                        </ListItemAvatar>
                        <ListItemText primary={user.name || user.id} secondary={user.designation} />
                        <IconButton onClick={(e) => { e.stopPropagation(); makeCall(user.id); }}>
                          <CallIcon color="success" />
                        </IconButton>
                      </ListItem>
                    )}
                  </For>
                </List>
              </Box>

              <Box sx={{ p: 2, borderTop: "1px solid #ccc", display: "flex", gap: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Enter number..."
                  value={dialNumber()}
                  onChange={(e) => setDialNumber(e.target.value)}
                />
                <Button variant="contained" color="success" onClick={() => makeCall(dialNumber())}>
                  Dial
                </Button>
              </Box>
            </Show>
          </Paper>
        </Show>
      </Box>

      <Show when={errorMessage()}>
        <Paper
          elevation={4}
          onClick={() => setErrorMessage("")}
          sx={{
            position: "fixed",
            bottom: 20,
            left: "50%",
            transform: "translateX(-50%)",
            bgcolor: "grey.900",
            color: "white",
            px: 2,
            py: 1,
            borderRadius: 1,
            zIndex: 10000,
            cursor: "pointer",
          }}
        >
          <Typography variant="body2">{errorMessage()}</Typography>
        </Paper>
      </Show>
    </Show>
  );
}
